import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BoxForm, SelectAutocomplete, StackForm, Switch } from 'components/Form'
import { SelectOption } from 'components/Select'
import { AmazonPageContext } from 'features/globalSetup/features/amazon/contexts/AmazonPageContext'
import { usePrevious } from 'hooks/usePrevious'
import { useAbility } from 'providers/casl'
import { DomainAmazonOption } from '../../hooks/useGetDomainAmazons'
import { Schema } from '../../schema'
import { optionsMapper } from './utils'

type Props = {
  options: DomainAmazonOption[]
}

export const DomainSection = ({ options }: Props) => {
  const ability = useAbility()
  const { t } = useTranslation('features/globalSetup/amazon', {
    keyPrefix: 'pages.inventoryConnection.basicAttributes',
  })
  const { setActiveAbTestId } = useContext(AmazonPageContext)
  const { control, getValues, reset, setValue, watch } = useFormContext<Schema>()
  const { id } = watch()
  const prevOptions = usePrevious<DomainAmazonOption[]>(options)

  const currentOption: DomainAmazonOption | undefined = options.find(
    (option: DomainAmazonOption) => option.value === id.value,
  )

  useEffect(() => {
    // select first option on start
    if (
      (prevOptions === undefined || prevOptions.length === 0) &&
      options.length > 0 &&
      id.value === ''
    ) {
      reset({
        ...getValues(),
        id: options[0],
      })
    }
  }, [prevOptions, options])

  useEffect(() => {
    // update 'enabled' field when Domain option is changed
    if (currentOption !== undefined && currentOption.value.length > 0) {
      setValue('enabled', currentOption.enabled)
    }
  }, [currentOption])

  useEffect(() => {
    if (currentOption !== undefined && setActiveAbTestId !== undefined) {
      setActiveAbTestId(currentOption.activeAbTestId)
    }
  }, [currentOption])

  const isDisabled: boolean = ability.cannot('update', 'GlobalSetupFeature-amazon')

  return (
    <BoxForm>
      <StackForm>
        <SelectAutocomplete<Schema, DomainAmazonOption>
          control={control}
          label={t('id.label')}
          name='id'
          onChange={(selectedOption: SelectOption<DomainAmazonOption>): void =>
            setValue('id', selectedOption, { shouldDirty: false })
          }
          options={optionsMapper(options)}
        />

        <Switch
          control={control}
          disabled={isDisabled}
          label={t('enabled.label')}
          labelPlacement='start'
          name='enabled'
        />
      </StackForm>
    </BoxForm>
  )
}
